// src/components/chemistry/Beaker.jsx
import { useDrop } from "react-dnd";
import { ItemTypes } from "../../../utils/constants";

export default function Beaker({
  onDrop,
  colorState,
  reactionComplete,
  shake,
  activeEffect,
}) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.REAGENT,
    drop: (item) => {
      if (!reactionComplete && item && item.id) onDrop(item); // full item
      return {};
    },
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  }));

  const gradients = {
    pink: ["from-pink-200", "to-pink-400"],
    green: ["from-green-200", "to-green-400"],
    brown: ["from-amber-300", "to-yellow-600"],
    red: ["from-rose-200", "to-rose-400"],
    orange: ["from-amber-300", "to-orange-500"],
    gray: ["from-gray-200", "to-gray-400"],
    blue: ["from-blue-200", "to-blue-400"],
  };

  const gradient = gradients[colorState] ?? gradients.blue;

  const liquidColors = {
    pink: "bg-pink-500/60",
    green: "bg-green-500/60",
    brown: "bg-amber-700/60",
    red: "bg-rose-500/60",
    orange: "bg-orange-500/60",
    gray: "bg-gray-400/60",
    blue: "bg-blue-400/60",
  };

  return (
    <div className="relative flex justify-center items-center">
      <div
        ref={drop}
        className={[
          "relative flex flex-col items-center justify-end h-64 w-52 rounded-b-[48%] border-[5px] border-blue-200 bg-gradient-to-b",
          gradient.join(" "),
          "shadow-[inset_0_0_25px_rgba(0,0,0,0.25)] transition-all duration-700 ease-in-out",
          isOver ? "ring-4 ring-yellow-400 scale-105" : "",
          shake ? "animate-[wiggle_0.4s_ease-in-out_infinite]" : "",
        ].join(" ")}
      >
        {/* FIRE effect - bright and obvious */}
        {activeEffect === "fire" && !reactionComplete && (
          <>
            <div className="absolute -bottom-7 flex gap-1">
              <div className="w-8 h-8 rounded-full bg-gradient-to-t from-orange-600 via-amber-400 to-yellow-300 opacity-80 blur-[2px] animate-pulse" />
              <div className="text-4xl animate-bounce -mt-3">🔥</div>
            </div>
          </>
        )}

        {/* SMOKE effect */}
        {activeEffect === "smoke" && !reactionComplete && (
          <div className="absolute -top-10 flex flex-col items-center gap-1">
            <div className="text-4xl animate-bounce">💨</div>
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-white/50 blur-sm animate-ping" />
              <div className="w-4 h-4 rounded-full bg-white/40 blur-sm animate-ping" />
            </div>
          </div>
        )}

        {/* BUBBLES effect */}
        {(activeEffect === "bubbles" || activeEffect?.id === "bubbles") &&
          !reactionComplete && (
            <div className="absolute bottom-10 flex flex-col items-center gap-1 pointer-events-none">
              {/* BIG BUBBLE EMOJI */}
              <div className="text-4xl animate-bounce">🫧</div>

              {/* SMALL FLOATING BUBBLES */}
              <div className="flex gap-4 mt-2">
                <div className="flex flex-col gap-2 items-center">
                  <div className="w-3 h-3 rounded-full bg-white/80 animate-bounce" />
                  <div className="w-2 h-2 rounded-full bg-white/70 animate-bounce delay-100" />
                </div>
                <div className="flex flex-col gap-2 items-center">
                  <div className="w-4 h-4 rounded-full bg-white/80 animate-bounce delay-150" />
                  <div className="w-2 h-2 rounded-full bg-white/60 animate-bounce delay-200" />
                </div>
                <div className="flex flex-col gap-2 items-center">
                  <div className="w-3 h-3 rounded-full bg-white/80 animate-bounce delay-300" />
                  <div className="w-2 h-2 rounded-full bg-white/60 animate-bounce delay-500" />
                </div>
              </div>
            </div>
          )}

        {/* LIQUID */}
        <div
          className={[
            "absolute bottom-0 left-0 w-full rounded-b-[48%] transition-all duration-1000 ease-in-out",
            liquidColors[colorState] || liquidColors.blue,
          ].join(" ")}
          style={{ height: reactionComplete ? "60%" : "35%" }}
        />

        {reactionComplete && (
          <div className="absolute top-1/2 -translate-y-1/2 text-white font-bold animate-pulse text-center text-base drop-shadow">
            ✅ Reaction Complete!
          </div>
        )}
      </div>
    </div>
  );
}
